import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';

export interface IOpenFormulareDialogConfig {
  idOpenFormulareDialogConfig?: number;
  idLegalTransactionType: number | null;
  Notary_idUser: number | null;
  Employee_idUser: number | null;
  UserGroup_idUserGroup: number | null;
  isAutoImport: boolean;
  isAutoImportComplete?: boolean;
}

export interface IOpenFormulareDialogEntry {
  DialogId: string;
  DialogTitle: string;
  description?: string;
  category?: string;
  isActive: boolean;
  isSystem?: boolean;
  isOrphan?: boolean;
  config: IOpenFormulareDialogConfig | null;
}

export interface IOpenFormulareDialogList {
  ofReachable: boolean;
  dialogs: IOpenFormulareDialogEntry[];
}

const QUERY_KEY = ['openformulare', 'dialogs'];

export const useGetOpenFormulareDialogs = () => {
  const { data, isLoading, refetch } = useQuery<IOpenFormulareDialogList>({
    queryKey: QUERY_KEY,
    queryFn: () => customFetcher({ endpoint: '/openformulare/dialogs' }),
    refetchOnWindowFocus: false,
  });
  return {
    ofDialogs: data,
    isLoadingOfDialogs: isLoading,
    refetchOfDialogs: refetch,
  };
};

export const useUpsertOpenFormulareDialogConfig = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (vars: {
      dialogId: string;
      body: Partial<IOpenFormulareDialogConfig> & {
        DialogTitle?: string;
      };
    }) =>
      customFetcher({
        endpoint: `/openformulare/dialogs/${encodeURIComponent(vars.dialogId)}`,
        method: 'PUT',
        body: vars.body,
      }) as Promise<IOpenFormulareDialogList>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
  return {
    upsertOfDialogConfig: mutateAsync,
    isUpsertingOfDialogConfig: isPending,
  };
};

export const useDeleteOpenFormulareDialogConfig = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (dialogId: string) =>
      customFetcher({
        endpoint: `/openformulare/dialogs/${encodeURIComponent(dialogId)}`,
        method: 'DELETE',
      }) as Promise<IOpenFormulareDialogList>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
  return {
    deleteOfDialogConfig: mutateAsync,
    isDeletingOfDialogConfig: isPending,
  };
};
